import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useParams, useLocation } from "react-router-dom";
import { Image, Link, Flex } from "uikit-react";
import moment from "moment";
import { yupResolver } from "@hookform/resolvers/yup";
import { useForm } from "react-hook-form";
import * as yup from "yup";
import UIkit from "uikit";
import classnames from "classnames";
import debounce from "lodash.debounce";

import { AppContexts } from "../../../providers";

import Loading from "../../common/Loading";
import WorkOrderComment from "../work_order_comment";
import ChecklistSection from "./ChecklistSection";
import CostsCard from "./CostsCard";
import CommentsSection from "./CommentsSection";

const schema = yup.object().shape({
  description: yup.string().nullable(),
  // summary: yup.string().required(),
  // status: yup.string().required(),
  // priority: yup.string().required(),
});

const normalizeChecklist = (list = []) =>
  (Array.isArray(list) ? list : []).map((it, i) => {
    const type = it.item_type || "yes_no";
    
    const completion = it.completion || {};
    
    let value = completion.value !== undefined ? completion.value : null;

    const base = {
      id: it.id ?? i,
      type,
      item_type: type,
      label: it.label || "",
      description: it.description || "",
      is_required: !!it.is_required,
      require_photo: !!it.require_photo,
      require_note: !!it.require_note,
      config: it.config || {},
      status: completion.status || 'not_started',
      value: value,
      notes: completion.notes || it.notes || '',
      completed_at: completion.completed_at,
      completed_by: completion.completed_by,
      media: Array.isArray(it.media) ? it.media : [],
    };

    if (type === "yes_no") {
      return { ...base, value: value ?? it.value ?? null };
    }

    if (type === "count") {
      return {
        ...base,
        value: value ?? it.value ?? (typeof it.value === "number" ? it.value : null),
        min: it.config?.min ?? it.min ?? 0,
        max: it.config?.max ?? it.max ?? null,
      };
    }

    if (type === "rating") {
      return {
        ...base,
        rating_value: value ?? it.rating_value ?? (typeof it.value === "number" ? it.value : null),
        scale_min: it.config?.scale_min ?? it.scale_min ?? 1,
        scale_max: it.config?.scale_max ?? it.scale_max ?? 5,
      };
    }

    if (type === "condition") {
      return {
        ...base,
        value: value ?? it.value ?? (typeof it.value === "string" ? it.value : ""),
      };
    }

    if (type === "list") {
      return {
        ...base,
        value: value ?? it.value ?? null,
      };
    }

    return base;
  });

const WorkOrderUpdate = () => {
  const params = useParams();
  const location = useLocation();
  const isTaskRoute = location.pathname.startsWith("/operations/tasks");
  const itemLabel = isTaskRoute ? "Task" : "Work Order";
  const itemLabelLower = itemLabel.toLowerCase();

  const workOrderContext = useContext(AppContexts.WorkOrderContext);
  const utilContext = useContext(AppContexts.UtilContext);
  const { refresh, user } = useContext(AppContexts.AuthContext);
  const { workOrder, loading } = workOrderContext;
  const [attachmentList, setAttachments] = useState(null);
  const [checklist, setChecklist] = useState([]);
  const [checklistTemplateId, setChecklistTemplateId] = useState(null);
  const [materialCost, setMaterialCost] = useState("");
  const [laborCost, setLaborCost] = useState("");
  const [totalCost, setTotalCost] = useState("");
  const [savingCosts, setSavingCosts] = useState(false);  
  const [openNotesId, setOpenNotesId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [savingCompletion, setSavingCompletion] = useState(false);
  const [lastSaved, setLastSaved] = useState(null);

  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(schema),
  });

  useEffect(() => {
    document.title = `${itemLabel} #${params.id} - RueBaRue`;
    // workOrderContext.getWorkOrderByAssigner(params.id, query.get("slug"));
    if (user) {
      if (params.id) {
        workOrderContext.getWorkOrder(params.id);
      }
    } else {
      if (params.id && params.slug) {
        workOrderContext.getWorkOrder(params.id, params.slug);
      }
    }
  }, [params, itemLabel]);


  useEffect(() => {
    if (!workOrder) {
      setChecklist([]);
      setChecklistTemplateId(null);
      return;
    }

    if (workOrder.checklist_items && Array.isArray(workOrder.checklist_items)) {
      setChecklist(normalizeChecklist(workOrder.checklist_items));
      if (workOrder.checklist_instance?.template_id) {
        setChecklistTemplateId(workOrder.checklist_instance.template_id);
      }

      // Sync checklist status if there's a mismatch for started work orders
      if (workOrder.checklist_instance && 
          workOrder.status === 'started' && 
          workOrder.checklist_instance.status === 'not-started') {
        workOrderContext.trackWorkOrder(
          workOrder.id,
          { action: "start" },
          params.slug
        ).catch(err => {
          console.error("Error syncing checklist status:", err);
        });
      }
    } else {
      setChecklist([]);
    }
  }, [workOrder, workOrderContext, params.slug]);

  useEffect(() => {
    if (!workOrder) return;

    setMaterialCost(
      workOrder.material_cost != null ? (workOrder.material_cost / 100).toFixed(2) : ""
    );
    setLaborCost(
      workOrder.labor_cost != null ? (workOrder.labor_cost / 100).toFixed(2) : ""
    );
    setTotalCost(
      workOrder.total_cost != null ? (workOrder.total_cost / 100).toFixed(2) : ""
    );
  }, [workOrder]);


  const updateWorkOrderStatus = useCallback(
    async (nextStatus) => {
      if (!workOrder) return;
      setSaving(true);
      try {
        await workOrderContext.updateWorkOrder(workOrder.id, {
          ...workOrder,
          status: nextStatus,
        });
      } finally {
        setSaving(false);
      }
    },
    [workOrder, workOrderContext]
  );

  const handleStart = useCallback(async () => {
    if (!workOrder || saving || workOrder.status !== 'new') return;
    try {
      setSaving(true);
      await workOrderContext.trackWorkOrder(
        workOrder.id,
        { action: "start" },
        params.slug
      );
      
      if (user) {
        await workOrderContext.getWorkOrder(workOrder.id);
      } else {
        await workOrderContext.getWorkOrder(workOrder.id, params.slug);
      }
      
      UIkit.notification(`${itemLabel} started`, { status: "success" });
    } catch (err) {
      console.error("Error starting work order:", err);
      UIkit.notification(`Failed to start ${itemLabelLower}`, { status: "danger" });
    } finally {
      setSaving(false);
    }
  }, [workOrder, saving, workOrderContext, params.slug, user, itemLabel, itemLabelLower]);

  const handleComplete = useCallback(async () => {
    if (!workOrder || saving || workOrder.status !== 'started') return;
    try {
      setSaving(true);
      await workOrderContext.trackWorkOrder(
        workOrder.id,
        { action: "complete" },
        params.slug
      );
      
      if (user) {
        await workOrderContext.getWorkOrder(workOrder.id);
      } else {
        await workOrderContext.getWorkOrder(workOrder.id, params.slug);
      }
      
      UIkit.notification(`${itemLabel} completed`, { status: "success" });
    } catch (err) {
      console.error("Error completing work order:", err);
      const missingRequirements = err.response?.data?.missingRequirements;
      let errorMessage = `Failed to complete ${itemLabelLower}`;
      if (missingRequirements && missingRequirements.length > 0) {
        const itemsList = missingRequirements
          .map(item => `• ${item.item_label}: ${item.reason}`)
          .join("<br>");
        errorMessage = `<strong>Cannot Complete Checklist</strong><br>${itemsList}`;
      }
      UIkit.notification(errorMessage, { status: "danger" });
    } finally {
      setSaving(false);
    }
  }, [workOrder, saving, workOrderContext, params.slug, user, itemLabel, itemLabelLower]);

  const checklistTotals = useMemo(() => {
    const total = checklist.length;
    const completed = checklist.filter((i) => i.status === "complete").length;
    return { total, completed };
  }, [checklist]);

  const checklistPct = useMemo(() => {
    if (!checklistTotals.total) return 0;
    return Math.round((checklistTotals.completed / checklistTotals.total) * 100);
  }, [checklistTotals]);

  const setItemStatus = (id, status) => {
    setChecklist((prev) => {
      const next = prev.map((i) => (i.id === id ? { ...i, status } : i));
      workOrderContext.updateWorkOrder(workOrder.id, { checklist: next });
      return next;
    });
  };

  const toggleNotes = useCallback((id) => {
    setOpenNotesId((prev) => (prev === id ? null : id));
  }, []);

  const toNumber = (val) => {
    const n = parseFloat((val || "").toString().replace(/[^0-9.-]/g, ""));
    return isNaN(n) ? 0 : n;
  };

  const formatMoney = (val) => {
    const n = toNumber(val);
    return n === 0 && (val === "" || val === null) ? "" : n.toFixed(2);
  };

  const calculateAndSetTotalCost = (material, labor) => {
    const materialNum = toNumber(material);
    const laborNum = toNumber(labor);
    const calculatedTotal = materialNum + laborNum;
    setTotalCost(calculatedTotal > 0 ? calculatedTotal.toFixed(2) : "");
  };

  const handleMaterialChange = (e) => {
    const val = e.target.value;
    setMaterialCost(val);
    calculateAndSetTotalCost(val, laborCost);
  };

  const handleLaborChange = (e) => {
    const val = e.target.value;
    setLaborCost(val);
    calculateAndSetTotalCost(materialCost, val);
  };

  const handleTotalChange = (e) => {
    const val = e.target.value;
    setTotalCost(val);
  };

  const handleSaveCosts = async () => {
    if (!workOrder) return;
    try {
      setSavingCosts(true);

      const costsData = {
        material_cost:
          materialCost === "" ? null : Math.round(Number(formatMoney(materialCost)) * 100),
        labor_cost:
          laborCost === "" ? null : Math.round(Number(formatMoney(laborCost)) * 100),
        total_cost:
          totalCost === "" ? null : Math.round(Number(formatMoney(totalCost)) * 100),
      };

      await workOrderContext.updateCosts(workOrder.id, costsData, params.slug);

      setMaterialCost(formatMoney(materialCost));
      setLaborCost(formatMoney(laborCost));
      setTotalCost(formatMoney(totalCost));

      UIkit.notification("Costs saved", { status: "success" });
    } catch (err) {
      console.error(err);
      UIkit.notification("Error saving costs", { status: "danger" });
    } finally {
      setSavingCosts(false);
    }
  };

  // TODO: At the moment, saving single item completion immediately; change this
  // to batch save later on a timer or when user leaves the page
  const saveItemCompletion = useCallback(async (itemId, field, value) => {
    if (!workOrder?.id) return;
    
    const currentItem = checklist.find(item => item.id === itemId);
    if (!currentItem) return;
    
    setSavingCompletion(true);
    try {
      const itemData = {
        item_id: parseInt(itemId),
        value: field === 'value' ? value : (currentItem.value ?? null),
        notes: field === 'notes' ? value : (currentItem.notes || ''),
      };

      const response = await workOrderContext.completeChecklistItems(workOrder.id, { items: [itemData] }, params.slug);
      
      if (response.data) {
        if (response.data.checklist_items && Array.isArray(response.data.checklist_items)) {
          setChecklist(normalizeChecklist(response.data.checklist_items));
        }
      }
      
      setLastSaved(new Date());
    } catch (error) {
      console.error('Error saving checklist completion:', error);
      UIkit.notification('Failed to save checklist', { status: 'danger' });
    } finally {
      setSavingCompletion(false);
    }
  }, [workOrder?.id, workOrderContext, checklist]);

  const updateItemValue = useCallback((itemId, value, itemType) => {
    setChecklist(prev => {
      const next = prev.map(item => {
        if (item.id !== itemId) return item;
        
        return {
          ...item,
          value,
        };
      });
      
      return next;
    });
    
    saveItemCompletion(itemId, 'value', value);
  }, [saveItemCompletion]);

  const setItemNotes = useCallback((itemId, notes) => {
    setChecklist(prev => {
      const next = prev.map(i => (i.id === itemId ? { ...i, notes } : i));
      return next;
    });
  }, []);
  
  // Save notes (on blur)
  const saveItemNotes = useCallback(async (itemId, notes) => {
    await saveItemCompletion(itemId, 'notes', notes);
    setOpenNotesId(null);
  }, [saveItemCompletion]);

  const clearItem = useCallback((itemId) => {
    const clearedValue = 'cleared';
    setChecklist(prev => {
      const next = prev.map(item => {
        if (item.id !== itemId) return item;
        
        return {
          ...item,
          value: clearedValue,
        };
      });
      
      return next;
    });
    

    saveItemCompletion(itemId, 'value', clearedValue);
  }, [saveItemCompletion]);

  const addItemMedia = async (id, files) => {
    if (!files || !files.length) return;

    const uploaded = await Promise.all(
      Array.from(files).map((f) =>
        utilContext
          .uploadFile(
            f,
            `workorders/checklist/${utilContext.genUUID()}`
          )
          .then((url) => ({
            url,
            type: f.type.startsWith("video") ? "video" : "image",
            name: f.name,
          }))
      )
    );

    setChecklist((prev) => {
      const next = prev.map((i) =>
        i.id === id
          ? { ...i, media: [...(i.media || []), ...uploaded] }
          : i
      );

      // auto-save
      workOrderContext.updateWorkOrder(workOrder.id, { checklist: next });

      return next;
    });
  };

  const removeItemMedia = useCallback((id, url) => {
    setChecklist((prev) =>
      prev.map((i) =>
        i.id === id
          ? { ...i, media: (i.media || []).filter((m) => m.url !== url) }
          : i
      )
    );

    try {
      URL.revokeObjectURL(url);
    } catch (_) {}
  }, []);

  const saveChecklist = useCallback(async () => {
    try {
      const { total, completed } = checklistTotals;

      // send the updated checklist + counts back to the API
      const updated = {
        ...workOrder,
        checklist: {
          template_id: checklistTemplateId,
          items: checklist,
        },
        checklist_total_items: total,
        checklist_completed_items: completed,
      };

      await workOrderContext.updateWorkOrder(workOrder.id, updated);
      UIkit.notification("Checklist saved", { status: "success" });
    } catch (err) {
      console.error(err);
      UIkit.notification("Error saving checklist", { status: "danger" });
    }
  }, [checklist, checklistTemplateId, checklistTotals, workOrder, workOrderContext]);

  const CLIENT_ONLY = true;

  const saveChecklistDebounced = useMemo(() => {
    if (CLIENT_ONLY) {
      const fn = () => {};
      fn.cancel = () => {};
      return fn;
    }

    const d = debounce(async (updatedChecklist, woId, templateId) => {
      if (!woId) return;
      await workOrderContext.updateWorkOrder(woId, { 
        checklist: {
          template_id: templateId,
          items: updatedChecklist,
        }
      });
    }, 400);

    return d;
  }, [CLIENT_ONLY, workOrderContext]);

  useEffect(() => {
    if (CLIENT_ONLY) return;
    return () => saveChecklistDebounced.cancel?.();
  }, [saveChecklistDebounced]);

  useEffect(() => {
    return () => {
      saveChecklistDebounced.cancel();
    };
  }, [saveChecklistDebounced]);

  const updateChecklistItem = useCallback((id, patch) => {
    setChecklist((prev) => {
      const next = prev.map((it) => (it.id === id ? { ...it, ...patch } : it));
      if (!CLIENT_ONLY) saveChecklistDebounced(next, workOrder?.id);
      return next;
    });
  }, [saveChecklistDebounced, workOrder?.id]);

  const handleFilesSelected = (files) => {
    if (!files || !files.length) return;
    const incoming = Array.from(files);

    setAttachments((prev) => {
      // if prev is a FileList/null, normalize to array
      const existing = prev ? Array.from(prev) : [];
      return [...existing, ...incoming];
    });
  };

  const handleRemoveFile = (index) => {
    setAttachments((prev) => {
      if (!prev) return prev;
      const arr = Array.from(prev);
      arr.splice(index, 1);
      return arr.length ? arr : null;
    });
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();

    const dt = e.dataTransfer;
    if (!dt || !dt.files || !dt.files.length) return;

    handleFilesSelected(dt.files);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const printPage = () => {
    window.print();
  };

  const handleRegister = useCallback(
    async (data) => {
      const hasText = data.description && data.description.trim().length > 0;
      const hasFiles = attachmentList && attachmentList.length > 0;
      if (!hasText && !hasFiles) return;

      try {
        if (attachmentList) {
          const files = [...attachmentList];
          const urls = await Promise.all(
            files.map((file) =>
              utilContext.uploadFile(
                file,
                `workorders/attachments/${utilContext.genUUID()}`,
                params.slug ? { slug: params.slug, work_order_id: params.id } : {},
              ),
            ),
          );
          const attachments = files.map((file, idx) => {
            return {
              type: "image",
              name: file.name,
              original_name: file.name,
              url: urls[idx],
            };
          });
          data["attachments"] = attachments;
        }

        await workOrderContext.commentWorkOrder(params.id, data, params.slug);
        setValue("description", "");
        setAttachments(null);
      } catch (err) {
        console.log(err);
      }
    },
    [workOrder, attachmentList, params],
  );

  const handleError = (err) => {
    console.log(err);
  };

  let { attachments, guest_reservation, rental } = workOrder || {};
  const isWorkOrderStarted = workOrder?.status === 'started'
  const isWorkOrderComplete = workOrder?.status === 'completed' || workOrder?.status === 'closed';
  const isWorkOrderUpdateDisabled = workOrder?.status === 'new' || isWorkOrderComplete;

  return (
    <div id="work-order-detail">
      {loading ? (
        <Loading />
      ) : (
        workOrder && (
          <>
            <div id="hms-page-title" className="uk-flex uk-flex-middle uk-flex-between">
              <h1 className="uk-heading-small">{itemLabel} #{workOrder.id}</h1>
              <img
                src="/images/print-no-bg.svg" 
                onClick={(e) => { e.preventDefault(); printPage(); }} 
                className="wo-print" 
                style={{ cursor: 'pointer' }}
              />              
            </div>

            <div id="hms-main-body">
              <div className="uk-modal-body uk-flex wo-background">
                <div className="wo-model-form-section">
                  <form onSubmit={handleSubmit(handleRegister, handleError)}>
                    <div className="uk-card uk-card-default uk-card-small wo-details-card">
                      <div className="uk-card-header">
                        <h3 className="uk-margin-remove" style={{ textTransform: "capitalize" }}>
                          {workOrder.summary}
                        </h3>
                      </div>

                      <div className="uk-card-body">
                        {/* Top meta: status / category / priority / reporter */}
                        <div className="uk-grid-small wo-meta-grid" data-uk-grid>
                          {workOrder.status && (
                            <div className="hms-search-result uk-width-1-2@s">
                              <div className="uk-form-label">Status</div>
                              <div className="wo-meta-value">
                                <span className="hms-booking-tag">
                                  {workOrder.status.replace("-", " ")}
                                </span>
                              </div>
                            </div>
                          )}

                          {workOrder.category && (
                            <div className="hms-search-result uk-width-1-2@s">
                              <div className="uk-form-label">Category</div>
                              <div className="wo-meta-value" style={{ textTransform: "capitalize" }}>
                                {workOrder.category}
                              </div>
                            </div>
                          )}

                          {workOrder.priority && (
                            <div className="hms-search-result uk-width-1-2@s">
                              <div className="uk-form-label">Priority</div>
                              <div className="wo-meta-value">
                                <span
                                  className={`hms-chip priority-${workOrder.priority.toLowerCase()}`}
                                >
                                  {workOrder.priority.charAt(0).toUpperCase() +
                                    workOrder.priority.slice(1)}
                                </span>
                              </div>
                            </div>
                          )}

                          {workOrder.reporter && (
                            <div className="hms-search-result uk-width-1-2@s">
                              <div className="uk-form-label">Reporter</div>
                              <div className="wo-meta-value">
                                {workOrder.reporter?.first_name} {workOrder.reporter?.last_name}
                              </div>
                            </div>
                          )}
                        </div>

                        {/* Description */}
                        {workOrder.description && (
                          <div className="hms-search-result uk-width-1-1 wo-description">
                            <div className="uk-form-label">Description</div>
                            <div className="wo-meta-value">{workOrder.description}</div>
                          </div>
                        )}

                        {/* Guest / Property / Attachments */}
                        <div className="uk-grid-small wo-secondary-grid" data-uk-grid>
                          {guest_reservation && (
                            <div className="hms-search-result uk-width-1-2@s wo-guest-card">
                              <div className="uk-form-label">Guest</div>
                              <div className="wo-meta-value">
                                <div className="wo-guest-name">
                                  {workOrder.guest_reservation.first_name}{" "}
                                  {workOrder.guest_reservation.last_name}
                                </div>
                                <div className="secondary">
                                  {moment
                                    .utc(workOrder.guest_reservation.check_in)
                                    .format("MMM DD, YYYY")}{" "}
                                  –{" "}
                                  {moment
                                    .utc(workOrder.guest_reservation.check_out)
                                    .format("MMM DD, YYYY")}
                                </div>
                                <div>{workOrder.guest_reservation.email}</div>
                                <div>{workOrder.guest_reservation.formatted_phone}</div>
                              </div>
                            </div>
                          )}

                          {rental && (
                            <div className="hms-search-result uk-width-1-2@s wo-property-card">
                              <div className="uk-form-label">Property</div>
                              <div className="wo-meta-value">
                                <div className="wo-property-name">{workOrder.rental.pms_id}</div>
                                <div className="wo-property-address">
                                  {workOrder.rental.address}
                                </div>
                              </div>
                            </div>
                          )}

                          {attachments && Array.isArray(attachments) && attachments.length > 0 && (
                            <div className="hms-search-result uk-width-1-1 wo-attachments">
                              <div className="uk-form-label">Attachments</div>
                              <div
                                className="uk-grid-small wo-attachments-grid"
                                data-uk-grid
                              >
                                {attachments.map((attachment) => (
                                  <div
                                    className="uk-width-1-3@s uk-width-1-4@m"
                                    key={attachment.url}
                                  >
                                    <div className="uk-card uk-card-default uk-card-small wo-attachment-card">
                                      <Image
                                        src={attachment.url}
                                        alt={attachment.name}
                                        className="wo-attachment-image"
                                      />
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* START BUTTON */}
                    <div className="uk-width-1-1 uk-margin-top no-print">
                      <div className="uk-card uk-card-default uk-card-small">
                        <div className="uk-card-body">
                          <button
                            className="uk-button uc-task-btn-start"
                            onClick={handleStart}
                            disabled={saving || isWorkOrderStarted || isWorkOrderComplete}
                          >
                              <span
                                className="uk-margin-small-right"
                                data-uk-icon="icon: play"
                              ></span>
                              {isWorkOrderStarted || isWorkOrderComplete ? 'Started' : `Start ${itemLabel}`}
                            </button>
                            {!isWorkOrderStarted && !isWorkOrderComplete && (
                              <div className="uk-text-meta">
                                Click Start {itemLabel} to begin the checklist, add notes or comments, and record costs.
                              </div>
                            )}
                          </div>
                        </div>
                    </div>

                    <ChecklistSection
                      checklist={checklist}
                      setChecklist={setChecklist}
                      checklistTotals={checklistTotals}
                      checklistPct={checklistPct}
                      isWorkOrderUpdateDisabled={isWorkOrderUpdateDisabled}
                      workOrder={workOrder}
                      savingCompletion={savingCompletion}
                      lastSaved={lastSaved}
                      openNotesId={openNotesId}
                      toggleNotes={toggleNotes}
                      updateItemValue={updateItemValue}
                      clearItem={clearItem}
                      saveItemCompletion={saveItemCompletion}
                      setItemNotes={setItemNotes}
                      saveItemNotes={saveItemNotes}
                    />

                    {/* COMMENTS & ATTACHMENTS */}
                    <CommentsSection
                      register={register}
                      attachmentList={attachmentList}
                      handleDragOver={handleDragOver}
                      handleDrop={handleDrop}
                      handleFilesSelected={handleFilesSelected}
                      handleRemoveFile={handleRemoveFile}
                    />

                    {/* COSTS CARD */}
                    <CostsCard
                      materialCost={materialCost}
                      laborCost={laborCost}
                      totalCost={totalCost}
                      handleMaterialChange={handleMaterialChange}
                      handleLaborChange={handleLaborChange}
                      handleTotalChange={handleTotalChange}
                      formatMoney={formatMoney}
                      setMaterialCost={setMaterialCost}
                      setLaborCost={setLaborCost}
                      setTotalCost={setTotalCost}
                      handleSaveCosts={handleSaveCosts}
                      savingCosts={savingCosts}
                    />

                    {/* COMPLETE BUTTON */}
                    <div className="uk-width-1-1 uk-margin-top no-print">
                      <div className="uk-card uk-card-default uk-card-small">
                        <div className="uk-card-body">
                          <button
                            className="uk-button uc-task-btn-complete"
                            onClick={handleComplete}
                            disabled={saving || isWorkOrderUpdateDisabled}
                          >
                              <span
                                className="uk-margin-small-right"
                                data-uk-icon="icon: check"
                              ></span>
                              {isWorkOrderComplete ? 'Completed' : `Complete ${itemLabel}`}
                            </button>
                            {!isWorkOrderComplete && (
                            <div className="uk-text-meta">
                              Click Complete {itemLabel} to mark the {itemLabelLower} as complete. All required checklist items must be finished first.
                            </div>
                            )} 
                          </div>
                        </div>
                    </div>
                  </form>
                </div>
                <WorkOrderComment order={workOrder} />
              </div>
            </div>
          </>
        )
      )}
    </div>
  );
};

export default WorkOrderUpdate;
